import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Layout from '../../components/common/Layout';
import { bookService } from '../../services/bookService';
import { BookFormData } from '../../types/book';
import { useAuth } from '../../context/AuthContext';
import {
  PhotoIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const bookSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title must be less than 200 characters'),
  author: z.string().min(1, 'Author is required').max(100, 'Author must be less than 100 characters'),
  genre: z.string().min(1, 'Genre is required'),
  description: z.string().min(10, 'Description must be at least 10 characters').max(1000, 'Description must be less than 1000 characters'),
  acquisitionPrice: z.number().min(0, 'Price must be positive'),
  acquiredFrom: z.enum(['Amazon', 'Gifted', 'Purchased', 'Airport', 'Other']),
  purchasedBy: z.string().min(1, 'Purchased by is required'),
  status: z.enum(['available', 'borrowed', 'lost', 'donated']),
});

type BookFormValues = z.infer<typeof bookSchema>;

const AddBook: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string>('');
  const [existingCoverUrl, setExistingCoverUrl] = useState<string>('');
  
  const isEditing = Boolean(id);
  const isAdmin = user?.role === 'admin';

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<BookFormValues>({
    resolver: zodResolver(bookSchema),
    defaultValues: {
      title: '',
      author: '',
      genre: '',
      description: '',
      acquisitionPrice: 0,
      acquiredFrom: 'Amazon',
      purchasedBy: '',
      status: 'available'
    }
  });

  useEffect(() => {
    // Only admins can edit existing books, but anyone can create new books
    if (isEditing && !isAdmin) {
      navigate('/books');
      return;
    }

    if (isEditing && id) {
      loadBookData(id);
    }
  }, [id, isEditing, isAdmin, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBookData = async (bookId: string) => {
    try {
      setLoading(true);
      const book = await bookService.getBookById(bookId);
      if (book) {
        reset({
          title: book.title,
          author: book.author,
          genre: book.genre,
          description: book.description,
          acquisitionPrice: book.acquisitionPrice,
          acquiredFrom: book.acquiredFrom,
          purchasedBy: book.purchasedBy,
          status: book.status
        });
        if (book.coverImageUrl) {
          setExistingCoverUrl(book.coverImageUrl);
        }
      }
    } catch (error) {
      console.error('Error loading book:', error);
      alert('Failed to load book data');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setCoverImage(file);
      const reader = new FileReader();
      reader.onload = () => {
        setCoverImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setCoverImage(null);
    setCoverImagePreview('');
    setExistingCoverUrl('');
  };

  const onSubmit = async (data: BookFormValues) => {
    if (!user) return;

    try {
      setLoading(true);
      const formData: BookFormData = {
        ...data,
        coverImage: coverImage || undefined
      };

      if (isEditing && id) {
        await bookService.updateBook(id, formData);
      } else {
        await bookService.createBook(formData, user.id);
      }

      navigate('/books');
    } catch (error: any) {
      console.error('Error saving book:', error);
      
      // Check if this is a storage-related error
      if (error.message?.includes('Firebase Storage is not set up')) {
        const shouldContinueWithoutImage = window.confirm(
          'Firebase Storage is not set up yet, so the cover image cannot be uploaded.\n\n' +
          'Would you like to save the book without the cover image?\n\n' +
          'You can add the image later after enabling Firebase Storage.'
        );
        
        if (shouldContinueWithoutImage) {
          try {
            // Save without the image  
            const formDataWithoutImage: BookFormData = {
              ...data,
              coverImage: undefined
            };
            
            if (isEditing && id) {
              await bookService.updateBook(id, formDataWithoutImage);
            } else {
              await bookService.createBook(formDataWithoutImage, user.id);
            }
            
            navigate('/books');
            return;
          } catch (retryError) {
            console.error('Error saving book without image:', retryError);
            alert('Failed to save book. Please try again.');
          }
        }
      } else {
        alert('Failed to save book. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const genres = [
    'Fiction',
    'Non-Fiction',
    'Science Fiction',
    'Fantasy',
    'Mystery',
    'Romance',
    'Thriller',
    'Biography',
    'History',
    'Science',
    'Technology',
    'Business',
    'Self-Help',
    'Art',
    'Poetry'
  ];

  // Only block access if editing and not admin
  if (isEditing && !isAdmin) {
    return null;
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Book' : 'Add New Book'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {isEditing ? 'Update book information' : 'Add a new book to your collection'}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            {/* Cover Image Upload */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Book Cover (Optional)
              </label>
              
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Cover images are optional. You can add them to make your books more visually appealing.
                </p>
              </div>
              
              {(coverImagePreview || existingCoverUrl) ? (
                <div className="relative inline-block">
                  <img
                    src={coverImagePreview || existingCoverUrl}
                    alt="Book cover preview"
                    className="w-32 h-48 object-cover rounded-lg border border-gray-300"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="cover-image"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                      >
                        <span>Upload a cover image</span>
                        <input
                          id="cover-image"
                          name="cover-image"
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          onChange={handleImageChange}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                  </div>
                </div>
              )}
            </div>

            {/* Book Details */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                  Title *
                </label>
                <input
                  type="text"
                  id="title"
                  {...register('title')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Enter book title"
                />
                {errors.title && (
                  <p className="mt-2 text-sm text-red-600">{errors.title.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="author" className="block text-sm font-medium text-gray-700">
                  Author *
                </label>
                <input
                  type="text"
                  id="author"
                  {...register('author')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Enter author name"
                />
                {errors.author && (
                  <p className="mt-2 text-sm text-red-600">{errors.author.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="genre" className="block text-sm font-medium text-gray-700">
                  Genre *
                </label>
                <select
                  id="genre"
                  {...register('genre')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                >
                  <option value="">Select a genre</option>
                  {genres.map((genre) => (
                    <option key={genre} value={genre}>
                      {genre}
                    </option>
                  ))}
                </select>
                {errors.genre && (
                  <p className="mt-2 text-sm text-red-600">{errors.genre.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="acquisitionPrice" className="block text-sm font-medium text-gray-700">
                  Acquisition Price *
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="number"
                    id="acquisitionPrice"
                    step="0.01"
                    min="0"
                    {...register('acquisitionPrice', { valueAsNumber: true })}
                    className="block w-full pl-7 pr-12 border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="0.00"
                  />
                </div>
                {errors.acquisitionPrice && (
                  <p className="mt-2 text-sm text-red-600">{errors.acquisitionPrice.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="acquiredFrom" className="block text-sm font-medium text-gray-700">
                  Acquired From *
                </label>
                <select
                  id="acquiredFrom"
                  {...register('acquiredFrom')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                >
                  <option value="Amazon">Amazon</option>
                  <option value="Gifted">Gifted</option>
                  <option value="Purchased">Purchased</option>
                  <option value="Airport">Airport</option>
                  <option value="Other">Other</option>
                </select>
                {errors.acquiredFrom && (
                  <p className="mt-2 text-sm text-red-600">{errors.acquiredFrom.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="purchasedBy" className="block text-sm font-medium text-gray-700">
                  Purchased By *
                </label>
                <input
                  type="text"
                  id="purchasedBy"
                  {...register('purchasedBy')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Who purchased this book?"
                />
                {errors.purchasedBy && (
                  <p className="mt-2 text-sm text-red-600">{errors.purchasedBy.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                  Status *
                </label>
                <select
                  id="status"
                  {...register('status')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                >
                  <option value="available">Available</option>
                  <option value="borrowed">Borrowed</option>
                  <option value="lost">Lost</option>
                  <option value="donated">Donated</option>
                </select>
                {errors.status && (
                  <p className="mt-2 text-sm text-red-600">{errors.status.message}</p>
                )}
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description *
                </label>
                <textarea
                  id="description"
                  rows={4}
                  {...register('description')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder="Enter book description..."
                />
                {errors.description && (
                  <p className="mt-2 text-sm text-red-600">{errors.description.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/books')}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-primary-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : (isEditing ? 'Update Book' : 'Add Book')}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default AddBook; 